import type { AIActionRisk, AIActionVerb, AIChatErrorCode, AIProgressStep } from "@k8s-dashboard/contracts";

/**
 * 사유 → 메시지 ID 표 (ADR 0026 · ADR 0025)
 * --------------------------------------------------------------------------
 * 서버는 문장을 보내지 않습니다. 안정 사유 코드만 오고 문구는 화면이 고릅니다.
 * 그 대응을 **리터럴 표**로 두는 이유는 둘입니다.
 *
 *   타입      `Record<AIChatErrorCode, string>`이라 코드가 하나 늘면 컴파일이 막습니다.
 *   게이트    ID가 템플릿 문자열이면 다국어 게이트가 "쓰이지 않는 문구"로 셉니다 —
 *             그러면 실제로 쓰는 문구가 조용히 지워질 수 있습니다.
 */

export const PROGRESS_MESSAGE: Record<AIProgressStep, string> = {
  reading: "agent.progress.reading",
  thinking: "agent.progress.thinking",
  answering: "agent.progress.answering",
};

export const ERROR_MESSAGE: Record<AIChatErrorCode, string> = {
  ai_disabled: "agent.error.ai_disabled",
  ai_invalid_request: "agent.error.ai_invalid_request",
  ai_rate_limited: "agent.error.ai_rate_limited",
  ai_budget_exceeded: "agent.error.ai_budget_exceeded",
  ai_busy: "agent.error.ai_busy",
  ai_upstream_unavailable: "agent.error.ai_upstream_unavailable",
  ai_session_not_found: "agent.error.ai_session_not_found",
  ai_store_unavailable: "agent.error.ai_store_unavailable",
  ai_action_not_supported: "agent.error.ai_action_not_supported",
  ai_replay_unavailable: "agent.error.ai_replay_unavailable",
};

/** 메시지 역할 → 라벨 ID. 역할은 둘뿐이고 계약이 그것을 고정합니다. */
export const ROLE_MESSAGE = {
  user: "agent.role.user",
  assistant: "agent.role.assistant",
} as const;

/** 참조 종류 → 라벨 ID. 계약의 kind가 정확히 이 둘입니다. */
export const REFERENCE_MESSAGE = {
  pod: "agent.reference.pod",
  workload: "agent.reference.workload",
} as const;

/**
 * 동사 → 문구 ID. (ADR 0027 Phase B)
 *
 * `Record<AIActionVerb, string>`이라 계약에 동사가 하나 늘면 **컴파일이 막습니다** —
 * 새 동사가 문구 없이 화면에 도달하거나, 더 나쁘게는 원문 그대로 렌더링되는 일이
 * 없습니다. 값이 필요한 동사는 문구가 자리표시자를 들고 있고, 채우는 값은 계약
 * 검증을 통과한 정수와 RFC 1123 이름뿐입니다.
 */
export const ACTION_VERB_MESSAGE: Record<AIActionVerb, string> = {
  RESTART_ROLLOUT: "agent.action.verb.RESTART_ROLLOUT",
  SCALE_REPLICAS: "agent.action.verb.SCALE_REPLICAS",
  ROLLBACK_REVISION: "agent.action.verb.ROLLBACK_REVISION",
  ADJUST_RESOURCES: "agent.action.verb.ADJUST_RESOURCES",
};

/** 위험도 → 문구 ID. 상태 색 예약어와 같은 축입니다. */
export const ACTION_RISK_MESSAGE: Record<AIActionRisk, string> = {
  LOW: "agent.action.risk.LOW",
  MEDIUM: "agent.action.risk.MEDIUM",
  HIGH: "agent.action.risk.HIGH",
};

/**
 * 위험도 → 글리프. 색만으로 의미를 전달하지 않기 위한 두 번째 채널이고,
 * 세 번째 채널인 텍스트 라벨은 항상 옆에 붙습니다. (design-system)
 */
export const ACTION_RISK_GLYPH: Record<AIActionRisk, string> = {
  LOW: "✓",
  MEDIUM: "!",
  HIGH: "▲",
};
